import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Typography } from "@mui/material";
import { createBrandTheme, Logo, colors } from "brand";
import {
  AccountMenu,
  AppBar,
  AppFooter,
  AppLayout,
  CognitoAuthProvider,
  ProtectedRoute,
  CallbackPage,
  LogoutPage,
  LoggedOutPage,
  ActiveOrgProvider,
  ActiveShowProvider,
  NavRail,
  OrgSwitcher,
  ShowSwitcher,
  useActiveOrg,
  UserDisplayName,
  BuildBadge,
  UsaBadge,
} from "common-fe";
import { OrganizationApi, Configuration } from "account-common";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useScoreApi, unwrap } from "./api/scoreApi";
import { AnnouncerProvider, useAnnouncerContext } from "./context/AnnouncerContext";
import { RingDataProvider, useRingData } from "./context/RingDataContext";
import AnnouncerRoleGate from "./components/AnnouncerRoleGate";
import AnnouncerFrame from "./components/AnnouncerFrame";
import DaySpine from "./components/DaySpine";
import LiveDesk from "./components/LiveDesk";
import LookupColumn from "./components/LookupColumn";
import StatusBar from "./components/StatusBar";
import WholeShowList from "./components/WholeShowList";
import { handleLogout } from "./utils/logout";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Inject the org-list fetcher (built from the generated account-common client)
// into the shared ActiveOrgProvider so announcer-fe sends the active-org
// X-Org-Id header on every score-api call — same pattern as scribe-fe /
// scoreadmin-fe. Without this the API falls back to the token's first org and
// an announcer with several org memberships would see the wrong show list.
const AnnouncerActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    async (isSuper) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations(true) // enabledOnly (#2074): switcher shows only superadminEnabled orgs
        : await api.listOrganizations();
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return <ActiveOrgProvider listOrgs={listOrgs}>{children}</ActiveOrgProvider>;
};

// #2112 — shared active-show, org-scoped, the same pattern scoreadmin-fe's
// ScoreadminActiveShowProvider uses. listShows is the generated score client
// (already X-Org-Id scoped); reloads when the active org changes. Replaces
// the old kiosk InitialSetup screen: the show is now a free switch from the
// app bar's ShowSwitcher, not a one-time, PIN-gated pick.
//
// #2190 — no local caching: a listShows() failure surfaces to
// ActiveShowProvider's own catch, same as any other consumer of that shared
// provider. NOTE this fetch is NOT one-shot-per-org-change in practice:
// ActiveShowProvider's effect deps are [listShows, orgId], and this
// provider's listShows memoises down to [accessToken, activeOrgId], so it
// re-runs on every access-token renew too, not only an org switch. A
// renew-time failure used to blank shows/activeShow mid-session with no
// self-heal until the next renew (#2215); ActiveShowProvider now keeps the
// last-good `shows` for the same org across a failed reload instead of
// resetting to [], so a transient renew-time failure leaves the running
// order on screen (stale, not blank) until the next successful reload.
const AnnouncerActiveShowProvider = ({ children }) => {
  const scoreApi = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const listShows = useCallback(async () => {
    return unwrap(await scoreApi.shows.listShows());
  }, [scoreApi]);
  return (
    // #2222 — announcer opts into the date-window filter (AC4): live shows,
    // not every show the org has ever ingested.
    <ActiveShowProvider listShows={listShows} orgId={activeOrgId} filterActive>
      {children}
    </ActiveShowProvider>
  );
};

function BrandLogoMark() {
  const { t } = useTranslation("announcer");
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Logo
        style={{
          paddingLeft: 8,
          height: 40,
          width: "auto",
          marginRight: 16,
          flexShrink: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );
}

// #2139 (docs/score/announcer-ui-redesign.md §4.1) — the shared NavRail is
// the ring switcher: one item per ring on the current show, icon-only at
// 72px, expanding to 200px on its own toggle (both widths and the expanded
// localStorage persistence come from common-fe's NavRailContext/NavRail —
// this app doesn't own any of that, see NavRail.jsx / NavRailContext.jsx).
// Ring is read/written straight off AnnouncerContext + RingDataContext, the
// SAME ungated switchRing the deleted arena Tabs row used to call (#926) —
// switching ring has never needed the PIN, and #2112 already removed the PIN
// entirely. This replaces the #1646 arena-tabs row that used to live inside
// OrderOfGo's sticky header; ring is navigation now, not part of one band's
// own header.
function AnnouncerShell({ children }) {
  const { t } = useTranslation("announcer");
  const navigate = useNavigate();
  const { mode, setMode } = useColorScheme();
  const { switchRing, ringResolved } = useAnnouncerContext();
  const { rings, selectedRing } = useRingData();

  const handleThemeToggle = () => {
    setMode(mode === "dark" ? "light" : "dark");
  };

  // One nav item per ring; icon is the ring number itself (spec: "the icon
  // is the ring number"), rendered as a small numbered badge rather than a
  // stock MUI icon since there is no icon that means "ring 3". The active
  // ring gets the same foxOrange + black-text treatment the deleted arena
  // Tabs used (legible in both themes, avoids brand primary.main's
  // near-black grey on dark, #862); `text` is the ring name shown when the
  // rail is expanded, same "Ring N" label the old tabs used.
  // #862 gotcha: no badgeContent is used here (ring numbers are never zero
  // in practice, they come straight off FVD's `class.ring` free text via
  // ringsFor), so there is nothing to guard with `|| undefined` — noted
  // per the card's explicit gotcha callout, not because a badge exists here.
  const navItems = rings.map((ring) => {
    const active = ring === selectedRing;
    return {
      icon: (
        <Box
          data-testid={`ring-tab-${ring}`}
          data-active={active}
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: "0.95rem",
            border: 1,
            borderColor: active ? colors.foxOrange : "divider",
            bgcolor: active ? colors.foxOrange : "transparent",
            color: active ? "#000" : "inherit",
          }}
        >
          {ring}
        </Box>
      ),
      text: `${t("setup.ringLabel")} ${ring}`,
      // #926 — ungated: switching a ring has never needed the PIN, and
      // #2112 removed the PIN entirely, so there's nothing left to gate.
      onClick: () => {
        if (ring !== selectedRing) switchRing(ring);
      },
    };
  });

  // #2141 (docs/score/announcer-ui-redesign.md §5) — the whole-show list's
  // own NavRail item, below the per-ring items (pushed last so NavRail's
  // in-order render puts it after them).
  navItems.push({
    icon: (
      <Box data-testid="nav-whole-show-list" sx={{ display: "flex" }}>
        <ListAltIcon />
      </Box>
    ),
    text: t("wholeShowList.navLabel"),
    onClick: () => navigate("/list"),
  });

  const accountMenuItems = [
    {
      icon: <SettingsIcon />,
      text: t("accountMenu.accountSettings"),
      onClick: () => {
        window.location.href = import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings";
      },
    },
    {
      icon: mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />,
      text: mode === "dark" ? t("accountMenu.lightMode") : t("accountMenu.darkMode"),
      onClick: handleThemeToggle,
    },
    {
      icon: <LogoutIcon />,
      text: t("accountMenu.signOut"),
      onClick: handleLogout,
    },
  ];

  const appBarRightContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <OrgSwitcher />
      <ShowSwitcher />
      <UserDisplayName />
      <AccountMenu menuItems={accountMenuItems} />
    </Box>
  );

  return (
    <AppLayout
      appBar={
        <AppBar
          sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}
          rightContent={appBarRightContent}
          brandLogo={<BrandLogoMark />}
        />
      }
      navRail={
        <Box data-testid="oog-ring" data-ring-resolved={ringResolved} sx={{ height: "100%" }}>
          <NavRail navItems={navItems} backgroundColor={colors.darkGrey} color="#fff" />
        </Box>
      }
      appFooterEnabled={true}
      appFooter={
        <AppFooter sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Fox Village.
          </Typography>
        </AppFooter>
      }
    >
      {children}
    </AppLayout>
  );
}

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  useColorScheme();

  return (
    <CognitoAuthProvider cognitoAuthConfig={cognitoAuthConfig} location={location}>
      <AnnouncerActiveOrgProvider>
        <Routes>
          <Route
            path="/callback"
            element={<CallbackPage navigateFunction={navigate} location={location} />}
          />
          <Route path="/logout" element={<LogoutPage navigateFunction={navigate} />} />
          <Route path="/logged-out" element={<LoggedOutPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AnnouncerRoleGate>
                  <AnnouncerActiveShowProvider>
                    <AnnouncerProvider>
                      {/* #2137 (docs/score/announcer-ui-redesign.md §11) — ONE
                          ring poll set for every band under this shell. Sits
                          inside AnnouncerProvider (it reads selectedShow/
                          selectedRing from there) and outside the Routes so a
                          later multi-band shell shares this same provider
                          instance rather than each band mounting its own. */}
                      <RingDataProvider>
                        <AnnouncerShell>
                          <Routes>
                            <Route path="/" element={<Navigate to="/oog" replace />} />
                            {/* #2145 (docs/score/announcer-ui-redesign.md §3) — the
                                four-band frame is now the main route. OrderOfGo.jsx
                                is deleted in this same pass, and so is the
                                provisional /frame route this used to live at
                                (#2138) — every band already has real content
                                (#2140/#2149/#2142), so there is no reason left for
                                the two routes to be separate. StatusBar (#2144)
                                re-homes here too, passed as the frame's own
                                statusBar slot (see AnnouncerFrame.jsx's own
                                comment for why it needed a new row rather than an
                                existing band). */}
                            <Route
                              path="/oog"
                              element={
                                <AnnouncerFrame
                                  statusBar={<StatusBar />}
                                  dayBand={<DaySpine />}
                                  liveDeskBand={<LiveDesk />}
                                  lookUpBand={<LookupColumn />}
                                />
                              }
                            />
                            {/* #2141 (docs/score/announcer-ui-redesign.md §5) —
                                the whole-show list: its own view, reached from
                                its own NavRail item, never mounted alongside
                                the four /oog bands (AC1 is true by
                                construction: this route and /oog are
                                mutually exclusive inside the same switch). */}
                            <Route path="/list" element={<WholeShowList />} />
                            <Route path="*" element={<Navigate to="/oog" replace />} />
                          </Routes>
                        </AnnouncerShell>
                      </RingDataProvider>
                    </AnnouncerProvider>
                  </AnnouncerActiveShowProvider>
                </AnnouncerRoleGate>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AnnouncerActiveOrgProvider>
    </CognitoAuthProvider>
  );
}

export default function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}
